import React, { useContext, useEffect, useState } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import uuid from "uuid";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Card } from "@material-ui/core";

import { UIMessageContext } from "../../components/UIMessage";
import { PageRoot, PageHeader, Link } from "../../components";
import { Suggestion } from "../../components/inputs/TypeaheadSelect";
import routes from "../../constants/routes";
import {
  MAX_FILE_SIZE,
  GENERAL_FILE_TYPES,
  COMPLETED_FORM_TYPES,
  COMPLETED_FORM_HINT,
  GENERAL_FILE_HINT
} from "../../constants/files";
import { INFORMATION_ACCURACY_CONFIRMATION } from "../../constants/messages";

import { regulatoryInstrumentActions } from "../../reducers/regulatoryInstrument";
import { Premises, LegalEntity } from "../../constants/interfaces";
import { premisesActions } from "../../reducers/premises";

import { RxForm, FormValues, FieldValueOnChange } from "../../components/Form";
import FormTextField from "../../components/Fields/FormTextField";
import SelectField from "../../components/Fields/SelectField";
import FileUploadField from "../../components/Fields/FileUploadField";
import { SubmitButton } from "../../components/Form/SubmitButton";
import { ResetButton } from "../../components/Form/ResetButton";
import { OnUnSaveChange } from "../../components/Form/OnUnSaveChange";
import Step from "./Step";
import api from "../../utils/api";
import { Button } from "../../components/inputs/Button";

const useStyles = makeStyles(theme => ({
  dropzone: {
    marginBottom: theme.spacing(2)
  },
  stepWrapper: {
    display: "flex",
    flexFlow: "row wrap"
  },
  typeahead: {
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(4),
    minWidth: "300px"
  },
  moreInformation: {
    display: "flex",
    alignItems: "center"
  },
  formCard: {
    padding: theme.spacing(4)
  },
  form: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    flexDirection: "column"
  },
  title: {
    marginBottom: theme.spacing(4)
  },
  hintText: {
    marginBottom: theme.spacing(2)
  },
  hintWrapper: {
    marginBottom: theme.spacing(4)
  },
  buttonWrapper: {
    display: "flex",
    marginTop: theme.spacing(1),
    "&>button": {
      marginRight: "1.2em"
    }
  }
}));

type Props = {
  classes?: any;
  loading: boolean | string;
  getPremises: () => Promise<void>;
  history: any;
  instrumentTypes: any;
  legalEntities: LegalEntity[];
  premises: Premises[];
  submitInstrument: (instrument: any, callback?: Function) => Promise<void>;
};

const ApplyRI: React.FC<Props> = props => {
  const classes = useStyles();
  const [tempCaseId] = useState(uuid());
  const [supportDocUUID] = useState(uuid());
  const { showMessage } = useContext(UIMessageContext);

  useEffect(() => {
    props.getPremises();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <PageRoot>
      <PageHeader
        breadcrumbLinks={[
          {
            label: routes.REGULATORY_INSTRUMENTS.label,
            route: routes.REGULATORY_INSTRUMENTS.url
          },
          {
            label: "Apply"
          }
        ]}
        label={routes.REGULATORY_INSTRUMENTS.label}
      />

      <Card className={classes.formCard}>
        <RxForm
          className={classes.form}
          confirmMessage={INFORMATION_ACCURACY_CONFIRMATION}
          onSubmit={async (value, meta, { resetForm }) => {
            const {
              instrumentType,
              legalEntity,
              premise,
              supportingFiles,
              applicantReference
            } = value;

            const instrument = {
              cmAcmCaseIdentifier: tempCaseId,
              legalEntityId: legalEntity
                ? legalEntity.cmAcmCaseIdentifier
                : null,
              premisesId: premise ? premise.cmAcmCaseIdentifier : null,
              riTypeId: instrumentType.value,
              supportingDocsStagingId: supportingFiles.length
                ? supportDocUUID
                : null,
              applicantReference: applicantReference
            };

            await props.submitInstrument(instrument, () => {
              resetForm();
              props.history.push(
                `${routes.REGULATORY_INSTRUMENTS.url}?tab=inProgress`
              );
              showMessage(
                "Your response was successfully submitted",
                "success"
              );
            });
          }}
        >
          <OnUnSaveChange />
          <FieldValueOnChange>
            {({ action, formState, updateFormValues }) => {
              if (
                action.name === "legalEntity" &&
                action.type === "@@FORM_CHANGE"
              ) {
                const nextFormValues = {
                  ...formState.values,
                  premise: null
                };
                updateFormValues(nextFormValues);
              } else if (
                action.name === "premise" &&
                action.type === "@@FORM_CHANGE"
              ) {
                const formLegalEntity = formState.values.legalEntity;
                if (formLegalEntity) return null;
                const propsLegalEntity = props.premises.find(
                  p =>
                    p.cmAcmCaseIdentifier === action.payload.cmAcmCaseIdentifier
                );
                const nextFormValues = {
                  ...formState.values,
                  legalEntity: (propsLegalEntity as Premises).parentLegalEntity
                };
                updateFormValues(nextFormValues);
              }

              return null;
            }}
          </FieldValueOnChange>

          <Typography
            className={classes.title}
            color="textSecondary"
            component="h2"
            variant="h5"
          >
            {routes.APPLY_RI.label}
          </Typography>
          <div className={classes.hintWrapper}>
            <Typography className={classes.hintText} color="textSecondary">
              Visit the resources page to download and complete your application form before proceeding with this process.
            </Typography>
            <Link
              id="resources"
              label="Visit resources"
              url={routes.RESOURCES.url}
            />
          </div>

          <FormValues>
            {({ formValues }) => {
              const { legalEntity } = formValues;

              const legalEntitiesSelectList = props.premises.reduce(
                (result: Suggestion[], premise) => {
                  const le = premise.parentLegalEntity;
                  if (!le || !le.viewable) return result;
                  if (
                    result.some(
                      r =>
                        r.value.cmAcmCaseIdentifier === le.cmAcmCaseIdentifier
                    )
                  )
                    return result;
                  result.push({
                    label: le.legalEntityName,
                    value: le
                  });
                  return result;
                },
                []
              );

              const premisesSelectList = props.premises
                .filter(premise => {
                  if (!premise.parentLegalEntity) return false;
                  if (!legalEntity) return true;
                  return (
                    premise.parentLegalEntity.cmAcmCaseIdentifier ===
                      (legalEntity as LegalEntity).cmAcmCaseIdentifier
                  );
                })
                .map(premise => ({
                  label: premise.premisesName,
                  value: premise
                }));

              return (
                <Step index={1} instruction="Select your premises">
                  <div className={classes.stepWrapper}>
                    <SelectField
                      id="legal-entity"
                      label="Legal Entity"
                      name="legalEntity"
                      getSelectedValue={selected =>
                        selected ? selected.value : null
                      }
                      className={classes.typeahead}
                      placeholder="Select legal entity"
                      getValueDisplay={value =>
                        value ? value.legalEntityName : ""
                      }
                      suggestions={legalEntitiesSelectList}
                    />
                    <SelectField
                      id="premise"
                      label="Premises"
                      name="premise"
                      getSelectedValue={selected =>
                        selected ? selected.value : null
                      }
                      className={classes.typeahead}
                      placeholder="Select premises"
                      getValueDisplay={value =>
                        value ? value.premisesName : ""
                      }
                      suggestions={premisesSelectList}
                    />
                  </div>
                </Step>
              );
            }}
          </FormValues>
          <Step index={2} instruction="Select regulatory instrument type *">
            <div className={classes.stepWrapper}>
              <SelectField
                required
                className={classes.typeahead}
                id="instrumentType"
                name="instrumentType"
                label="Instrument type"
                placeholder="Select instrument type"
                suggestions={Object.entries(props.instrumentTypes)
                  .map(pair => ({
                    label: pair[1] as string,
                    value: pair[0]
                  }))}
                getSelectedValue={selected => (selected ? selected : undefined)}
                getValueDisplay={value => (value ? value.label : undefined)}
              />
            </div>
          </Step>
          <Step
            index={3}
            instruction="Upload your completed application form *"
          >
            <FileUploadField
              required
              allowMultipleFiles={false}
              handleError={error => {
                showMessage(
                  _.get(error, "response.data.message") || error.message,
                  "error"
                );
              }}
              onFileUpload={(files: File[]) => {
                const formData: any = new FormData();
                formData.append("uploadfile", files[0]);
                return api.post(`/ri/uploadFile/${tempCaseId}`, formData);
              }}
              onFileRemove={(file: File) =>
                api.put(`/ri/removeFile/${tempCaseId}`, file.name, {
                  headers: {
                    "Content-Type": "application/json"
                  }
                })
              }
              acceptedFileTypes={COMPLETED_FORM_TYPES}
              className={classes.dropzone}
              hint={COMPLETED_FORM_HINT}
              name="competedForm"
              id="competedForm"
              maxFileCount={1}
              maxTotalByteSize={MAX_FILE_SIZE}
            />
          </Step>

          <Step index={4} instruction="Attach any supporting documents">
            <FileUploadField
              handleError={error => {
                showMessage(
                  _.get(error, "response.data.message") || error.message,
                  "error"
                );
              }}
              onFileUpload={(files: File[]) =>
                Promise.all(
                  files.map(file => {
                    const formData: any = new FormData();
                    formData.append("uploadfile", file);
                    return api.post(
                      `/ri/uploadFile/${supportDocUUID}`,
                      formData
                    );
                  })
                )
              }
              onFileRemove={(file: File) =>
                api.put(`/ri/removeFile/${supportDocUUID}`, file.name, {
                  headers: {
                    "Content-Type": "application/json"
                  }
                })
              }
              acceptedFileTypes={GENERAL_FILE_TYPES}
              className={classes.dropzone}
              hint={GENERAL_FILE_HINT}
              name="supportingFiles"
              id="supportingFiles"
              maxFileCount={10}
              maxTotalByteSize={MAX_FILE_SIZE}
            />
          </Step>

          <Step index={5} instruction="Add your applicant reference">
            <div className={classes.stepWrapper}>
              <FormTextField
                  id="applicantReference"
                  name="applicantReference"
                  label="Applicant Reference"
                  inputProps={{ maxLength: 64 }}
                  className={classes.typeahead}
              />
            </div>
          </Step>

          <div className={classes.buttonWrapper}>
            <SubmitButton label="Submit" />
            <ResetButton
              label="Reset"
              onReset={() => {
                api.put(`/ri/removeFiles/${tempCaseId}`);
                api.put(`/ri/removeFiles/${supportDocUUID}`);
              }}
            />
            <Button
              onClick={() => {
                props.history.push(`${routes.REGULATORY_INSTRUMENTS.url}`);
              }}
              id="close"
            >
              Close
            </Button>
          </div>
        </RxForm>
      </Card>
    </PageRoot>
  );
};

const mapStateToProps = ({ regulatoryInstrument, premises }: any) => ({
  premises: premises.premises,
  instrumentTypes: regulatoryInstrument.instrumentTypes
});

const mapDispatchToProps = {
  getPremises: premisesActions.getPremises,
  submitInstrument: regulatoryInstrumentActions.submitRegulatoryInstrument
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ApplyRI);
